import React, { useContext, useState, useEffect, useMemo, useCallback } from "react";
import {
  Box,
  Button,
  Typography,
  Grid,
  Card,
  CardContent,
  CardActions,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  InputAdornment,
  Chip,
  CircularProgress,
  IconButton,
  Avatar,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Skeleton,
} from "@mui/material";
import {
  SaveAlt,
  People,
  Business,
  Assignment,
  Category,
  Group,
  Visibility,
  Search,
  Close,
  Refresh,
  Clear,
  Badge as BadgeIcon,
  Star,
  School,
  QrCode,
  Engineering,
  BarChart as BarChartIcon,
  PieChart as PieIcon,
} from "@mui/icons-material";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip as RechartsTooltip,
  PieChart,
  Pie,
  Cell,
  Legend,
  CartesianGrid,
} from "recharts";
import axios from "axios";
import BackBtn from "../../widgets/BackBtn";
import { ExportContext } from "../../context/ExportProvider";
import { LoadingButton } from "@mui/lab";
import { ADMIN, API_ENDPOINT, USER_ROLES } from "../../utils/constants";
import { getHeader } from "../../utils/utils2";
import { extractDate } from "../../utils/util";
import * as XLSX from "xlsx";

const EXPORT_MODULES = [
  {
    type: USER_ROLES.VISITOR,
    fileName: "Visitor_List",
    title: "Visitor List",
    description: "Export the list of all registered visitors in Excel format.",
    icon: <People fontSize="large" color="primary" />,
    color: "primary",
    badgeBg: "#EFF6FF",
    badgeColor: "#2563EB",
  },
  {
    type: USER_ROLES.EXHIBITOR,
    fileName: "Exhibitor_List",
    title: "Exhibitor List",
    description: "Export the list of all exhibitors in Excel format.",
    icon: <Business fontSize="large" color="secondary" />,
    color: "secondary",
    badgeBg: "#FDF2F8",
    badgeColor: "#DB2777",
  },
  {
    type: USER_ROLES.TRADE_DELEGATE,
    fileName: "Trade_Delegates",
    title: "Trade Delegates",
    description: "Export the list of trade delegates in Excel format.",
    icon: <BadgeIcon fontSize="large" sx={{ color: "#0284C7" }} />,
    color: "info",
    badgeBg: "#F0F9FF",
    badgeColor: "#0284C7",
  },
  {
    type: USER_ROLES.SPECIAL_INVITEE,
    fileName: "Special_Invitees",
    title: "Special Invitees",
    description: "Export the list of special invitees in Excel format.",
    icon: <Star fontSize="large" sx={{ color: "#8B5CF6" }} />,
    color: "secondary",
    badgeBg: "#F5F3FF",
    badgeColor: "#7C3AED",
  },
  {
    type: USER_ROLES.STUDENT,
    fileName: "Students_List",
    title: "Students List",
    description: "Export the list of student attendees in Excel format.",
    icon: <School fontSize="large" sx={{ color: "#F59E0B" }} />,
    color: "warning",
    badgeBg: "#FFFBEB",
    badgeColor: "#D97706",
  },
  {
    type: "service_code",
    fileName: "Service_Codes",
    title: "Service Codes Data",
    description: "Export the list of service codes & service personnel.",
    icon: <QrCode fontSize="large" sx={{ color: "#10B981" }} />,
    color: "success",
    badgeBg: "#ECFDF5",
    badgeColor: "#059669",
  },
  {
    type: "staff",
    fileName: "Staff_List",
    title: "Staffs Data",
    description: "Export the list of staff badges and team staff.",
    icon: <Engineering fontSize="large" sx={{ color: "#EC4899" }} />,
    color: "primary",
    badgeBg: "#FDF2F8",
    badgeColor: "#DB2777",
  },
  {
    type: "oes",
    fileName: "OES_Forms_Data",
    title: "OES Forms Data",
    description: "Export OES forms submission data in Excel format.",
    icon: <Assignment fontSize="large" color="success" />,
    color: "success",
    badgeBg: "#F0FDF4",
    badgeColor: "#16A34A",
  },
  {
    type: "product",
    fileName: "Product_List",
    title: "Product List",
    description: "Export the list of products in Excel format.",
    icon: <Category fontSize="large" color="warning" />,
    color: "warning",
    badgeBg: "#FEF3C7",
    badgeColor: "#B45309",
  },
  {
    type: "team",
    fileName: "Team_Members_List",
    title: "Team Members List",
    description: "Export the list of team members in Excel format.",
    icon: <Group fontSize="large" color="info" />,
    color: "info",
    badgeBg: "#E0F2FE",
    badgeColor: "#0369A1",
  },
];

const INDIVIDUAL_SUMMARY_CARDS = [
  {
    key: USER_ROLES.VISITOR,
    title: "Visitors",
    subtext: "Registered Visitors",
    icon: <People />,
    color: "#2563EB",
    bg: "#EFF6FF",
  },
  {
    key: USER_ROLES.EXHIBITOR,
    title: "Exhibitors",
    subtext: "Registered Companies",
    icon: <Business />,
    color: "#DB2777",
    bg: "#FDF2F8",
  },
  {
    key: USER_ROLES.TRADE_DELEGATE,
    title: "Trade Delegates",
    subtext: "Delegate Attendees",
    icon: <BadgeIcon />,
    color: "#0284C7",
    bg: "#F0F9FF",
  },
  {
    key: USER_ROLES.SPECIAL_INVITEE,
    title: "Special Invitees",
    subtext: "VIP & Special Guests",
    icon: <Star />,
    color: "#7C3AED",
    bg: "#F5F3FF",
  },
  {
    key: USER_ROLES.STUDENT,
    title: "Students",
    subtext: "Student Attendees",
    icon: <School />,
    color: "#D97706",
    bg: "#FFFBEB",
  },
  {
    key: "service_code",
    title: "Service Codes",
    subtext: "Service Personnel",
    icon: <QrCode />,
    color: "#059669",
    bg: "#ECFDF5",
  },
  {
    key: "staff",
    title: "Staff Badges",
    subtext: "Team Staff Badges",
    icon: <Engineering />,
    color: "#EC4899",
    bg: "#FDF2F8",
  },
  {
    key: "oes",
    title: "OES Submissions",
    subtext: "Forms Submitted",
    icon: <Assignment />,
    color: "#16A34A",
    bg: "#F0FDF4",
  },
  {
    key: "product",
    title: "Products Catalog",
    subtext: "Cataloged Products",
    icon: <Category />,
    color: "#B45309",
    bg: "#FEF3C7",
  },
  {
    key: "team",
    title: "Team Members",
    subtext: "System Admin Team",
    icon: <Group />,
    color: "#0369A1",
    bg: "#E0F2FE",
  },
];

const ExportDataPage = () => {
  const [currentExport, setCurrentExport] = useState(-1);
  const {
    exportVisitors,
    visitorExporting,
    exportExhibitors,
    exhibitorExporting,
    exportProducts,
    productExporting,
    exportOes,
    oesExporting,
    exportTm,
    tmExporting,
  } = useContext(ExportContext);

  // Counts state for all 10 modules
  const [counts, setCounts] = useState({});
  const [loadingCounts, setLoadingCounts] = useState(true);

  // View modal state
  const [activeModalItem, setActiveModalItem] = useState(null);
  const [modalData, setModalData] = useState([]);
  const [loadingData, setLoadingData] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  // Category & Column Specific Filter States
  const [filter1, setFilter1] = useState("ALL");
  const [filter2, setFilter2] = useState("ALL");

  // Fetch summary counts for all modules
  const fetchAllCounts = useCallback(async () => {
    setLoadingCounts(true);
    const newCounts = {};

    try {
      // 1. Visitors, Trade Delegates, Special Invitees, Students, Staff
      const visitorTypes = [
        USER_ROLES.VISITOR,
        USER_ROLES.TRADE_DELEGATE,
        USER_ROLES.SPECIAL_INVITEE,
        USER_ROLES.STUDENT,
        "staff",
      ];

      await Promise.all(
        visitorTypes.map(async (t) => {
          try {
            const res = await axios.get(
              `${API_ENDPOINT}/admin/get-visitors?type=${t}&page=1&limit=1`,
              getHeader(ADMIN)
            );
            newCounts[t] =
              res.data?.totalItems ??
              res.data?.total ??
              res.data?.totalCount ??
              (Array.isArray(res.data?.visitors)
                ? res.data.visitors.length
                : Array.isArray(res.data?.data)
                ? res.data.data.length
                : Array.isArray(res.data)
                ? res.data.length
                : 0);
          } catch {
            newCounts[t] = 0;
          }
        })
      );

      // 2. Exhibitors
      try {
        const res = await axios.get(
          `${API_ENDPOINT}/user/a/get-all-exhibitors`,
          getHeader()
        );
        newCounts[USER_ROLES.EXHIBITOR] =
          res.data?.length ?? res.data?.data?.length ?? 0;
      } catch {
        newCounts[USER_ROLES.EXHIBITOR] = 0;
      }

      // 3. OES Forms
      try {
        const res = await axios.get(
          `${API_ENDPOINT}/admin/get-all-oes-submit`,
          getHeader(ADMIN)
        );
        newCounts["oes"] = res.data?.length ?? res.data?.data?.length ?? 0;
      } catch {
        newCounts["oes"] = 0;
      }

      // 4. Products
      try {
        const res = await axios.get(
          `${API_ENDPOINT}/admin/get-all-products`,
          getHeader(ADMIN)
        );
        newCounts["product"] = res.data?.length ?? res.data?.data?.length ?? 0;
      } catch {
        newCounts["product"] = 0;
      }

      // 5. Team Members
      try {
        const res = await axios.get(
          `${API_ENDPOINT}/admin/get-all-teammembers`,
          getHeader(ADMIN)
        );
        newCounts["team"] = res.data?.length ?? res.data?.data?.length ?? 0;
      } catch {
        newCounts["team"] = 0;
      }

      // 6. Service Codes
      try {
        const res = await axios.get(
          `${API_ENDPOINT}/admin/service-codes?page=1&limit=1`,
          getHeader()
        );
        newCounts["service_code"] =
          res.data?.totalItems ??
          res.data?.total ??
          res.data?.data?.length ??
          0;
      } catch {
        newCounts["service_code"] = 0;
      }

      setCounts(newCounts);
    } catch (err) {
      console.error("Failed to load counts", err);
    } finally {
      setLoadingCounts(false);
    }
  }, []);

  useEffect(() => {
    fetchAllCounts();
  }, [fetchAllCounts]);

  const exportServiceCodes = async (customData = null) => {
    try {
      let data = customData;
      if (!Array.isArray(data) || data.length === 0) {
        const res = await axios.get(
          `${API_ENDPOINT}/admin/service-codes?limit=1000`,
          getHeader()
        );
        data = res?.data?.data || res?.data || [];
      }
      const formatted = (Array.isArray(data) ? data : []).map((row) => ({
        "Service Code": row.serviceCode || row.code || "",
        "Company Name": row.companyName || row.exhibitorName || "",
        "Contact Person": row.contactPerson || row.name || "",
        "Email / Phone": row.email || row.phone || "",
        "Service Type": row.serviceType || "Service",
        "Exhibitor ID": row.exhibitorId || "",
        "Hall Number": row.hallNumber || "",
        "Stand Number": row.standNumber || "",
      }));
      const ws = XLSX.utils.json_to_sheet(formatted);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Service Codes");
      XLSX.writeFile(wb, "service_codes.xlsx");
    } catch (err) {
      console.error("Failed to export service codes", err);
    }
  };

  const exportToExcel = (index, customFilteredData = null) => {
    setCurrentExport(index);
    const item = EXPORT_MODULES[index];
    if (!item) return;

    let dataToExport = customFilteredData;
    if (!dataToExport && activeModalItem === index && Array.isArray(filteredModalData)) {
      dataToExport = filteredModalData;
    }

    if (item.type === USER_ROLES.EXHIBITOR) {
      exportExhibitors(dataToExport);
    } else if (item.type === "oes") {
      exportOes(dataToExport);
    } else if (item.type === "product") {
      exportProducts(dataToExport);
    } else if (item.type === "team") {
      exportTm(dataToExport);
    } else if (item.type === "service_code") {
      exportServiceCodes(dataToExport);
    } else {
      exportVisitors(item.type, "", searchQuery, dataToExport);
    }
  };

  const handleOpenViewModal = async (index) => {
    setActiveModalItem(index);
    setSearchQuery("");
    setFilter1("ALL");
    setFilter2("ALL");
    setLoadingData(true);
    setModalData([]);

    const item = EXPORT_MODULES[index];

    try {
      let res;
      if (item.type === USER_ROLES.EXHIBITOR) {
        res = await axios.get(
          `${API_ENDPOINT}/user/a/get-all-exhibitors`,
          getHeader()
        );
        const raw = res?.data?.data || res?.data || [];
        setModalData(Array.isArray(raw) ? raw : []);
      } else if (item.type === "oes") {
        res = await axios.get(
          `${API_ENDPOINT}/admin/get-all-oes-submit`,
          getHeader(ADMIN)
        );
        const raw = res?.data?.data || res?.data || [];
        setModalData(Array.isArray(raw) ? raw : []);
      } else if (item.type === "product") {
        res = await axios.get(
          `${API_ENDPOINT}/admin/get-all-products`,
          getHeader(ADMIN)
        );
        const raw = res?.data?.data || res?.data || [];
        setModalData(Array.isArray(raw) ? raw : []);
      } else if (item.type === "team") {
        res = await axios.get(
          `${API_ENDPOINT}/admin/get-all-teammembers`,
          getHeader(ADMIN)
        );
        const raw = res?.data?.data || res?.data || [];
        setModalData(Array.isArray(raw) ? raw : []);
      } else if (item.type === "service_code") {
        res = await axios.get(
          `${API_ENDPOINT}/admin/service-codes?limit=1000`,
          getHeader()
        );
        const raw = res?.data?.data || res?.data || [];
        setModalData(Array.isArray(raw) ? raw : []);
      } else {
        let allVisitorsModal = [];
        let p = 1;
        let fetchMore = true;
        while (fetchMore && p <= 100) {
          const res = await axios.get(
            `${API_ENDPOINT}/admin/get-visitors?type=${item.type}&page=${p}&limit=1000`,
            getHeader(ADMIN)
          );
          const rawData =
            res?.data?.data ||
            res?.data?.visitors ||
            (Array.isArray(res?.data) ? res.data : []);
          if (Array.isArray(rawData) && rawData.length > 0) {
            allVisitorsModal = [...allVisitorsModal, ...rawData];
            const totalItems =
              res?.data?.totalItems ??
              res?.data?.total ??
              res?.data?.totalCount;
            if (totalItems && allVisitorsModal.length >= totalItems) {
              fetchMore = false;
            } else if (rawData.length < 1000) {
              fetchMore = false;
            } else {
              p++;
            }
          } else {
            fetchMore = false;
          }
        }
        setModalData(allVisitorsModal);
      }
    } catch (err) {
      console.error("Failed to fetch data", err);
      setModalData([]);
    } finally {
      setLoadingData(false);
    }
  };

  const handleCloseModal = () => {
    setActiveModalItem(null);
    setModalData([]);
    setSearchQuery("");
    setFilter1("ALL");
    setFilter2("ALL");
  };

  const resetFilters = () => {
    setSearchQuery("");
    setFilter1("ALL");
    setFilter2("ALL");
  };

  // Data for Charts
  const barChartData = useMemo(() => {
    return EXPORT_MODULES.map((item) => ({
      name: item.title.replace(" Data", "").replace(" List", ""),
      count: counts[item.type] || 0,
      fill: item.badgeColor,
    }));
  }, [counts]);

  const pieChartData = useMemo(() => {
    return [
      { name: "Visitors", value: counts[USER_ROLES.VISITOR] || 0, color: "#2563EB" },
      { name: "Exhibitors", value: counts[USER_ROLES.EXHIBITOR] || 0, color: "#DB2777" },
      { name: "Trade Delegates", value: counts[USER_ROLES.TRADE_DELEGATE] || 0, color: "#0284C7" },
      { name: "Special Invitees", value: counts[USER_ROLES.SPECIAL_INVITEE] || 0, color: "#7C3AED" },
      { name: "Students", value: counts[USER_ROLES.STUDENT] || 0, color: "#D97706" },
      { name: "Staff & Service", value: (counts["staff"] || 0) + (counts["service_code"] || 0), color: "#059669" },
    ].filter((item) => item.value > 0 || !loadingCounts);
  }, [counts, loadingCounts]);

  // Dynamic extraction of distinct filter options based on activeModalItem
  const { filter1Options, filter2Options, filter1Label, filter2Label } =
    useMemo(() => {
      if (activeModalItem === null) {
        return { filter1Options: [], filter2Options: [], filter1Label: "", filter2Label: "" };
      }
      const current = EXPORT_MODULES[activeModalItem];
      if (!current) {
        return { filter1Options: [], filter2Options: [], filter1Label: "", filter2Label: "" };
      }

      if (current.type === "service_code") {
        const codes = Array.from(
          new Set(modalData.map((r) => r.serviceCode || r.code).filter(Boolean))
        );
        const comps = Array.from(
          new Set(modalData.map((r) => r.companyName || r.exhibitorName).filter(Boolean))
        );
        return {
          filter1Options: codes,
          filter2Options: comps,
          filter1Label: "Service Code",
          filter2Label: "Company",
        };
      }

      if (current.type === USER_ROLES.EXHIBITOR) {
        const halls = Array.from(
          new Set(modalData.map((r) => r.hallNumber?.value).filter(Boolean))
        );
        const spaces = Array.from(
          new Set(modalData.map((r) => r.spaceType?.value).filter(Boolean))
        );
        return {
          filter1Options: halls,
          filter2Options: spaces,
          filter1Label: "Hall Number",
          filter2Label: "Space Type",
        };
      }

      if (current.type === "oes") {
        const forms = Array.from(
          new Set(modalData.map((r) => r.name).filter(Boolean))
        );
        const suppliers = Array.from(
          new Set(
            modalData
              .map((r) => (r.selectedSupplier || r.supplier)?.supplierName)
              .filter(Boolean)
          )
        );
        return {
          filter1Options: forms,
          filter2Options: suppliers,
          filter1Label: "Form Name",
          filter2Label: "Supplier",
        };
      }

      if (current.type === "product") {
        const cats = Array.from(
          new Set(
            modalData
              .flatMap((r) =>
                (r.selectedCategories?.value || []).map((c) => c.subSubCat || c.mainCat)
              )
              .filter(Boolean)
          )
        );
        const owners = Array.from(
          new Set(modalData.map((r) => r.owner).filter(Boolean))
        );
        return {
          filter1Options: cats,
          filter2Options: owners,
          filter1Label: "Product Category",
          filter2Label: "Owner",
        };
      }

      if (current.type === "team") {
        const roles = Array.from(
          new Set(modalData.map((r) => r.role).filter(Boolean))
        );
        const desigs = Array.from(
          new Set(modalData.map((r) => r.designation).filter(Boolean))
        );
        return {
          filter1Options: roles,
          filter2Options: desigs,
          filter1Label: "Member Role",
          filter2Label: "Designation",
        };
      }

      const comps = Array.from(
        new Set(
          modalData
            .map(
              (r) =>
                (typeof r.companyName === "string"
                  ? r.companyName
                  : r.companyName?.value) ||
                r.company ||
                r.institute ||
                r.institution ||
                r.institutionName ||
                r.organization
            )
            .filter(Boolean)
        )
      );
      const desigs = Array.from(
        new Set(
          modalData
            .map((r) => r.designation || r.contactDesignation?.value || r.role || r.jobTitle)
            .filter(Boolean)
        )
      );
      return {
        filter1Options: comps,
        filter2Options: desigs,
        filter1Label: "Company / Institute",
        filter2Label: "Designation / Role",
      };
    }, [modalData, activeModalItem]);

  // Apply filters (Search + Filter 1 + Filter 2)
  const filteredModalData = useMemo(() => {
    if (activeModalItem === null) return [];
    const current = EXPORT_MODULES[activeModalItem];

    return modalData.filter((row) => {
      // 1. Search Query
      if (searchQuery.trim()) {
        const q = searchQuery.toLowerCase();
        const matchesSearch = Object.values(row).some((val) => {
          if (typeof val === "string") return val.toLowerCase().includes(q);
          if (typeof val === "number") return val.toString().includes(q);
          if (typeof val === "object" && val !== null) {
            return JSON.stringify(val).toLowerCase().includes(q);
          }
          return false;
        });
        if (!matchesSearch) return false;
      }

      // 2. Filter 1
      if (filter1 !== "ALL") {
        if (current.type === "service_code") {
          if ((row.serviceCode || row.code) !== filter1) return false;
        } else if (current.type === USER_ROLES.EXHIBITOR) {
          if (row.hallNumber?.value !== filter1) return false;
        } else if (current.type === "oes") {
          if (row.name !== filter1) return false;
        } else if (current.type === "product") {
          const rowCats = (row.selectedCategories?.value || []).map(
            (c) => c.subSubCat || c.mainCat
          );
          if (!rowCats.includes(filter1)) return false;
        } else if (current.type === "team") {
          if (row.role !== filter1) return false;
        } else {
          const c =
            (typeof row.companyName === "string"
              ? row.companyName
              : row.companyName?.value) ||
            row.company ||
            row.institute ||
            row.institution ||
            row.institutionName ||
            row.organization;
          if (c !== filter1) return false;
        }
      }

      // 3. Filter 2
      if (filter2 !== "ALL") {
        if (current.type === "service_code") {
          if ((row.companyName || row.exhibitorName) !== filter2) return false;
        } else if (current.type === USER_ROLES.EXHIBITOR) {
          if (row.spaceType?.value !== filter2) return false;
        } else if (current.type === "oes") {
          const s = (row.selectedSupplier || row.supplier)?.supplierName;
          if (s !== filter2) return false;
        } else if (current.type === "product") {
          if (row.owner !== filter2) return false;
        } else if (current.type === "team") {
          if (row.designation !== filter2) return false;
        } else {
          const d =
            row.designation ||
            row.contactDesignation?.value ||
            row.role ||
            row.jobTitle;
          if (d !== filter2) return false;
        }
      }

      return true;
    });
  }, [modalData, searchQuery, filter1, filter2, activeModalItem]);

  const hasActiveFilter =
    searchQuery.trim() !== "" || filter1 !== "ALL" || filter2 !== "ALL";

  // Render Table content based on active item index
  const renderTableContent = () => {
    if (loadingData) {
      return (
        <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
          <CircularProgress />
        </Box>
      );
    }

    if (filteredModalData.length === 0) {
      return (
        <Box sx={{ textAlign: "center", py: 6, color: "#64748B" }}>
          <Typography variant="body1">No matching records found.</Typography>
          {hasActiveFilter && (
            <Button
              size="small"
              onClick={resetFilters}
              sx={{ mt: 1, textTransform: "none" }}
            >
              Clear filters
            </Button>
          )}
        </Box>
      );
    }

    const current = EXPORT_MODULES[activeModalItem];

    if (current?.type === "service_code") {
      return (
        <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: "8px" }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow sx={{ bgcolor: "#F8FAFC" }}>
                <TableCell sx={{ fontWeight: 600 }}>Service Code</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Company Name</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Contact Person</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Email / Phone</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Service Type</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredModalData.map((row, i) => (
                <TableRow key={row._id || i} hover>
                  <TableCell sx={{ fontWeight: 600, color: "#059669" }}>
                    {row.serviceCode || row.code || "N/A"}
                  </TableCell>
                  <TableCell sx={{ fontWeight: 500 }}>
                    {row.companyName || row.exhibitorName || "N/A"}
                  </TableCell>
                  <TableCell>{row.contactPerson || row.name || "N/A"}</TableCell>
                  <TableCell>{row.email || row.phone || "N/A"}</TableCell>
                  <TableCell>
                    <Chip label={row.serviceType || "Service"} size="small" color="success" />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      );
    }

    if (current?.type === USER_ROLES.EXHIBITOR) {
      return (
        <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: "8px" }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow sx={{ bgcolor: "#F8FAFC" }}>
                <TableCell sx={{ fontWeight: 600 }}>Company Name</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Exhibitor ID</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Contact Person</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Email</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Mobile</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Hall / Stand</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredModalData.map((row, i) => (
                <TableRow key={row._id || i} hover>
                  <TableCell sx={{ fontWeight: 600, color: "#1E293B" }}>
                    {row.companyName?.value ||
                      (typeof row.companyName === "string" ? row.companyName : "") ||
                      row.username ||
                      "N/A"}
                  </TableCell>
                  <TableCell>{row.user_id || row._id || "N/A"}</TableCell>
                  <TableCell>
                    {row.contactFirstName?.value || ""} {row.contactLastName?.value || ""}
                  </TableCell>
                  <TableCell>
                    {row.companyEmail?.value ||
                      row.contactEmail?.value ||
                      (typeof row.companyEmail === "string" ? row.companyEmail : "") ||
                      (typeof row.email === "string" ? row.email : "") ||
                      "N/A"}
                  </TableCell>
                  <TableCell>
                    {row.mobile?.value ||
                      (typeof row.mobile === "string" ? row.mobile : "") ||
                      row.mobileNo ||
                      row.contactMobile?.value ||
                      row.phone?.value ||
                      (typeof row.phone === "string" ? row.phone : "") ||
                      "N/A"}
                  </TableCell>
                  <TableCell>
                    {row.hallNumber?.value ? `Hall ${row.hallNumber.value}` : ""}{" "}
                    {row.standNumber?.value ? `(${row.standNumber.value})` : "N/A"}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      );
    }

    if (current?.type === "oes") {
      return (
        <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: "8px" }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow sx={{ bgcolor: "#F8FAFC" }}>
                <TableCell sx={{ fontWeight: 600 }}>Form Name</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Submitted By</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Supplier</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Submitted On</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Object ID</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredModalData.map((row, i) => (
                <TableRow key={row._id || i} hover>
                  <TableCell sx={{ fontWeight: 600, color: "#2563EB" }}>
                    {row.name || "N/A"}
                  </TableCell>
                  <TableCell>{row.submittedBy || "N/A"}</TableCell>
                  <TableCell>
                    {(row.selectedSupplier || row.supplier)?.supplierName || "N/A"}
                  </TableCell>
                  <TableCell>{extractDate(row.createdOn) || "N/A"}</TableCell>
                  <TableCell sx={{ fontSize: "0.75rem", color: "#64748B" }}>
                    {row._id || "N/A"}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      );
    }

    if (current?.type === "product") {
      return (
        <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: "8px" }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow sx={{ bgcolor: "#F8FAFC" }}>
                <TableCell sx={{ fontWeight: 600 }}>Product Name</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Owner</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Categories</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Description</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Date</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredModalData.map((row, i) => {
                const cats = (row.selectedCategories?.value || [])
                  .map((c) => c.subSubCat)
                  .join(", ");
                return (
                  <TableRow key={row._id || i} hover>
                    <TableCell sx={{ fontWeight: 600, color: "#1E293B" }}>
                      {row.name || "N/A"}
                    </TableCell>
                    <TableCell>{row.owner || "N/A"}</TableCell>
                    <TableCell>
                      {cats ? (
                        <Chip label={cats} size="small" variant="outlined" color="primary" />
                      ) : (
                        "N/A"
                      )}
                    </TableCell>
                    <TableCell
                      sx={{
                        maxWidth: 300,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {row.description || "N/A"}
                    </TableCell>
                    <TableCell>{extractDate(row.createdOn) || "N/A"}</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      );
    }

    if (current?.type === "team") {
      return (
        <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: "8px" }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow sx={{ bgcolor: "#F8FAFC" }}>
                <TableCell sx={{ fontWeight: 600 }}>Member</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Email</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Phone</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Role</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Designation</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Created Date</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredModalData.map((row, i) => (
                <TableRow key={row._id || i} hover>
                  <TableCell>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                      <Avatar
                        src={row.profile_image}
                        sx={{
                          width: 32,
                          height: 32,
                          bgcolor: "#2563EB",
                          fontSize: 13,
                        }}
                      >
                        {row.firstName ? row.firstName[0] : "T"}
                      </Avatar>
                      <Typography variant="body2" fontWeight="600">
                        {row.firstName} {row.lastName}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>{row.email || "N/A"}</TableCell>
                  <TableCell>{row.phone || "N/A"}</TableCell>
                  <TableCell>
                    <Chip
                      label={row.role || "Member"}
                      size="small"
                      color="info"
                      sx={{ textTransform: "capitalize", fontWeight: 500 }}
                    />
                  </TableCell>
                  <TableCell>{row.designation || "N/A"}</TableCell>
                  <TableCell>{extractDate(row.createdOn) || "N/A"}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      );
    }

    return (
      <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: "8px" }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow sx={{ bgcolor: "#F8FAFC" }}>
              <TableCell sx={{ fontWeight: 600 }}>Name</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Email</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Mobile</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Company / Institute</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Designation</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Type</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredModalData.map((row, i) => {
              const fullName =
                `${row.title || ""} ${row.firstName || row.name || row.contactFirstName?.value || ""} ${row.lastName || row.contactLastName?.value || ""}`
                  .replace(/\s+/g, " ")
                  .trim() ||
                row.name ||
                "N/A";
              const mobile =
                row.mobileNo ||
                row.mobile ||
                row.phone?.value ||
                row.phone ||
                row.phoneNo ||
                row.contactNo ||
                "N/A";
              const company =
                (typeof row.companyName === "string"
                  ? row.companyName
                  : row.companyName?.value) ||
                row.company ||
                row.institute ||
                row.institution ||
                row.institutionName ||
                row.organization ||
                "N/A";
              const designation =
                row.designation ||
                row.contactDesignation?.value ||
                row.role ||
                row.jobTitle ||
                "N/A";

              return (
                <TableRow key={row._id || i} hover>
                  <TableCell sx={{ fontWeight: 500 }}>{fullName}</TableCell>
                  <TableCell>{row.email || row.contactEmail?.value || "N/A"}</TableCell>
                  <TableCell>{mobile}</TableCell>
                  <TableCell>{company}</TableCell>
                  <TableCell>{designation}</TableCell>
                  <TableCell>
                    <Chip
                      label={row.user_role || row.role || row.eventVisitor || current?.title}
                      size="small"
                      color={current?.color || "default"}
                      sx={{ fontWeight: 500 }}
                    />
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  const currentItem =
    activeModalItem !== null ? EXPORT_MODULES[activeModalItem] : null;

  return (
    <Box sx={{ pb: 6 }}>
      <BackBtn />

      {/* Header Section */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          mb: 3.5,
          flexWrap: "wrap",
          gap: 2,
        }}
      >
        <Box>
          <Typography
            variant="h4"
            sx={{ fontWeight: 800, color: "#0F172A", letterSpacing: "-0.02em" }}
          >
            Data Analytics & Export Center
          </Typography>
          <Typography variant="body2" sx={{ color: "#64748B", mt: 0.5 }}>
            Comprehensive overview, data visualization graphs, and instant Excel exports.
          </Typography>
        </Box>

        <Button
          variant="outlined"
          startIcon={<Refresh />}
          onClick={fetchAllCounts}
          disabled={loadingCounts}
          sx={{
            borderRadius: "8px",
            borderColor: "#E2E8F0",
            color: "#475467",
            textTransform: "none",
            fontWeight: 600,
            bgcolor: "#FFFFFF",
            "&:hover": { bgcolor: "#F8FAFC", borderColor: "#CBD5E1" },
          }}
        >
          Refresh Counts
        </Button>
      </Box>

      {/* ── 10 Individual Summary Cards Row ── */}
      <Grid container spacing={2} sx={{ mb: 4 }}>
        {INDIVIDUAL_SUMMARY_CARDS.map((card) => {
          const val = counts[card.key] || 0;
          return (
            <Grid item xs={12} sm={6} md={4} lg={2.4} key={card.key}>
              <Card
                sx={{
                  borderRadius: "10px",
                  border: "1px solid #E2E8F0",
                  boxShadow: "0 2px 8px rgba(0, 0, 0, 0.03)",
                  p: 2,
                  bgcolor: "#FFFFFF",
                  position: "relative",
                  overflow: "hidden",
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "space-between",
                  transition: "all 0.15s ease",
                  "&:hover": {
                    transform: "translateY(-2px)",
                    boxShadow: "0 6px 16px rgba(0, 0, 0, 0.06)",
                    borderColor: "#CBD5E1",
                  },
                }}
              >
                <Box
                  sx={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    width: "4px",
                    height: "100%",
                    bgcolor: card.color,
                  }}
                />

                <Box>
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <Typography variant="body2" sx={{ color: "#64748B", fontWeight: 600, fontSize: "0.825rem" }}>
                      {card.title}
                    </Typography>
                    <Avatar sx={{ bgcolor: card.bg, color: card.color, width: 34, height: 34 }}>
                      {React.cloneElement(card.icon, { fontSize: "small" })}
                    </Avatar>
                  </Box>

                  {loadingCounts ? (
                    <Skeleton width="60%" height={36} sx={{ my: 0.5 }} />
                  ) : (
                    <Typography variant="h4" sx={{ fontWeight: 800, color: "#0F172A", my: 0.5, fontSize: "1.75rem" }}>
                      {val.toLocaleString()}
                    </Typography>
                  )}
                </Box>

                <Typography variant="caption" sx={{ color: card.color, fontWeight: 600, fontSize: "0.725rem", mt: 0.5 }}>
                  {card.subtext}
                </Typography>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      {/* ── Data Visualization Graphs Section ── */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {/* Bar Chart Overview */}
        <Grid item xs={12} md={7}>
          <Card
            sx={{
              borderRadius: "10px",
              border: "1px solid #E2E8F0",
              boxShadow: "0 4px 12px rgba(0, 0, 0, 0.03)",
              p: 2.5,
              bgcolor: "#FFFFFF",
              height: "100%",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", justify: "space-between", mb: 2 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <BarChartIcon style={{ color: "#2563EB" }} />
                <Typography variant="h6" sx={{ fontWeight: 700, color: "#0F172A", fontSize: "1rem" }}>
                  Record Count Distribution by Module
                </Typography>
              </Box>
            </Box>

            <Box sx={{ width: "100%", height: 300, mt: 1 }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={barChartData} margin={{ top: 10, right: 10, left: -20, bottom: 40 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#F1F5F9" />
                  <XAxis
                    dataKey="name"
                    tick={{ fontSize: 11, fill: "#64748B" }}
                    angle={-30}
                    textAnchor="end"
                    interval={0}
                  />
                  <YAxis tick={{ fontSize: 11, fill: "#64748B" }} />
                  <RechartsTooltip
                    contentStyle={{
                      backgroundColor: "#FFFFFF",
                      borderRadius: "8px",
                      border: "1px solid #E2E8F0",
                      boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                    }}
                  />
                  <Bar dataKey="count" radius={[4, 4, 0, 0]}>
                    {barChartData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.fill} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </Box>
          </Card>
        </Grid>

        {/* Pie / Donut Chart */}
        <Grid item xs={12} md={5}>
          <Card
            sx={{
              borderRadius: "10px",
              border: "1px solid #E2E8F0",
              boxShadow: "0 4px 12px rgba(0, 0, 0, 0.03)",
              p: 2.5,
              bgcolor: "#FFFFFF",
              height: "100%",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
              <PieIcon style={{ color: "#7C3AED" }} />
              <Typography variant="h6" sx={{ fontWeight: 700, color: "#0F172A", fontSize: "1rem" }}>
                Attendee Breakdown Share
              </Typography>
            </Box>

            <Box sx={{ width: "100%", height: 300 }}>
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={pieChartData}
                    cx="50%"
                    cy="45%"
                    innerRadius={55}
                    outerRadius={85}
                    paddingAngle={3}
                    dataKey="value"
                  >
                    {pieChartData.map((entry, index) => (
                      <Cell key={`pie-cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <RechartsTooltip
                    contentStyle={{
                      backgroundColor: "#FFFFFF",
                      borderRadius: "8px",
                      border: "1px solid #E2E8F0",
                    }}
                  />
                  <Legend
                    layout="horizontal"
                    verticalAlign="bottom"
                    align="center"
                    wrapperStyle={{ fontSize: "11px", paddingTop: "10px" }}
                  />
                </PieChart>
              </ResponsiveContainer>
            </Box>
          </Card>
        </Grid>
      </Grid>

      {/* ── Export & View Modules Grid Section ── */}
      <Box sx={{ mb: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 700, color: "#0F172A", mb: 2 }}>
          Data Export & Management Modules ({EXPORT_MODULES.length})
        </Typography>

        <Grid container spacing={2.5}>
          {EXPORT_MODULES.map((item, index) => {
            const count = counts[item.type] || 0;
            return (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <Card
                  sx={{
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    borderRadius: "10px",
                    border: "1px solid #E2E8F0",
                    boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.04)",
                    transition: "all 0.2s ease",
                    "&:hover": {
                      boxShadow: "0 10px 15px -3px rgba(0, 0, 0, 0.08)",
                      borderColor: "#CBD5E1",
                    },
                  }}
                >
                  <CardContent sx={{ flexGrow: 1, p: 2.5 }}>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        mb: 1.5,
                      }}
                    >
                      <Box sx={{ display: "flex", alignItems: "center" }}>
                        {item.icon}
                        <Typography
                          variant="h6"
                          component="div"
                          sx={{ ml: 1.5, fontWeight: 700, fontSize: "1.05rem" }}
                        >
                          {item.title}
                        </Typography>
                      </Box>
                      <Chip
                        label={loadingCounts ? "..." : `${count.toLocaleString()}`}
                        size="small"
                        sx={{
                          fontWeight: 700,
                          fontSize: "0.75rem",
                          bgcolor: item.badgeBg,
                          color: item.badgeColor,
                        }}
                      />
                    </Box>
                    <Typography variant="body2" sx={{ color: "#64748B", fontSize: "0.85rem" }}>
                      {item.description}
                    </Typography>
                  </CardContent>

                  <CardActions sx={{ p: 2, pt: 0, gap: 1 }}>
                    <Button
                      fullWidth
                      variant="outlined"
                      color={item.color}
                      startIcon={<Visibility />}
                      onClick={() => handleOpenViewModal(index)}
                      sx={{
                        borderRadius: "8px",
                        textTransform: "none",
                        fontWeight: 600,
                      }}
                    >
                      View Data
                    </Button>
                    <LoadingButton
                      fullWidth
                      loading={
                        index === currentExport &&
                        (visitorExporting ||
                          exhibitorExporting ||
                          productExporting ||
                          tmExporting ||
                          oesExporting)
                      }
                      variant="contained"
                      color={item.color}
                      startIcon={<SaveAlt />}
                      onClick={() => exportToExcel(index)}
                      sx={{
                        borderRadius: "8px",
                        textTransform: "none",
                        fontWeight: 600,
                        boxShadow: "none",
                      }}
                    >
                      Export
                    </LoadingButton>
                  </CardActions>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      </Box>

      {/* Data Viewer & Filter Dialog */}
      <Dialog
        open={activeModalItem !== null}
        onClose={handleCloseModal}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "14px",
            p: 1,
            maxHeight: "90vh",
          },
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            pb: 1,
            pt: 1.5,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Typography variant="h6" sx={{ fontWeight: 700, color: "#0F172A" }}>
              {currentItem?.title}
            </Typography>
            {!loadingData && (
              <Chip
                label={`${filteredModalData.length} Records`}
                size="small"
                color={currentItem?.color || "primary"}
                sx={{ fontWeight: 600, fontSize: "0.75rem" }}
              />
            )}
          </Box>

          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <IconButton
              size="small"
              onClick={() => handleOpenViewModal(activeModalItem)}
              sx={{ color: "#64748B" }}
            >
              <Refresh size={18} />
            </IconButton>
            <IconButton size="small" onClick={handleCloseModal} sx={{ color: "#64748B" }}>
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ py: 1.5 }}>
          {/* Search & Filter Toolbar */}
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: 1.5,
              mb: 2.5,
              alignItems: "center",
              justifyContent: "space-between",
              bgcolor: "#F8FAFC",
              p: 2,
              borderRadius: "12px",
              border: "1px solid #E2E8F0",
            }}
          >
            <TextField
              size="small"
              placeholder={`Search ${currentItem?.title || "data"}...`}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search color="action" fontSize="small" />
                  </InputAdornment>
                ),
              }}
              sx={{
                flex: "1 1 240px",
                minWidth: 200,
                maxWidth: { xs: "100%", md: "400px" },
                "& .MuiOutlinedInput-root": {
                  borderRadius: "8px",
                  backgroundColor: "#FFFFFF",
                },
              }}
            />

            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5, alignItems: "center", width: { xs: "100%", md: "auto" } }}>
              {filter1Options.length > 0 && (
                <FormControl size="small" sx={{ minWidth: 170, maxWidth: { xs: "100%", sm: 210 }, flex: "1 1 170px" }}>
                  <InputLabel id="export-filter1-label">{filter1Label}</InputLabel>
                  <Select
                    labelId="export-filter1-label"
                    value={filter1}
                    label={filter1Label}
                    onChange={(e) => setFilter1(e.target.value)}
                    sx={{
                      borderRadius: "8px",
                      backgroundColor: "#FFFFFF",
                      "& .MuiSelect-select": {
                        textOverflow: "ellipsis",
                        overflow: "hidden",
                        whiteSpace: "nowrap",
                      },
                    }}
                  >
                    <MenuItem value="ALL">All {filter1Label}s</MenuItem>
                    {filter1Options.map((opt) => (
                      <MenuItem key={opt} value={opt}>
                        {opt}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}

              {filter2Options.length > 0 && (
                <FormControl size="small" sx={{ minWidth: 170, maxWidth: { xs: "100%", sm: 210 }, flex: "1 1 170px" }}>
                  <InputLabel id="export-filter2-label">{filter2Label}</InputLabel>
                  <Select
                    labelId="export-filter2-label"
                    value={filter2}
                    label={filter2Label}
                    onChange={(e) => setFilter2(e.target.value)}
                    sx={{
                      borderRadius: "8px",
                      backgroundColor: "#FFFFFF",
                      "& .MuiSelect-select": {
                        textOverflow: "ellipsis",
                        overflow: "hidden",
                        whiteSpace: "nowrap",
                      },
                    }}
                  >
                    <MenuItem value="ALL">All {filter2Label}s</MenuItem>
                    {filter2Options.map((opt) => (
                      <MenuItem key={opt} value={opt}>
                        {opt}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}

              {hasActiveFilter && (
                <Button
                  size="small"
                  variant="outlined"
                  color="error"
                  onClick={resetFilters}
                  startIcon={<Clear fontSize="small" />}
                  sx={{
                    textTransform: "none",
                    fontWeight: 600,
                    borderRadius: "8px",
                    px: 1.5,
                    height: "40px",
                    whiteSpace: "nowrap",
                  }}
                >
                  Clear Filters
                </Button>
              )}
            </Box>
          </Box>

          {renderTableContent()}
        </DialogContent>

        <DialogActions sx={{ p: 2, pt: 1, justifyContent: "space-between" }}>
          <Button onClick={handleCloseModal} variant="outlined" sx={{ borderRadius: "8px" }}>
            Close
          </Button>

          {currentItem && (
            <LoadingButton
              variant="contained"
              color={currentItem.color}
              startIcon={<SaveAlt />}
              loading={
                activeModalItem === currentExport &&
                (visitorExporting ||
                  exhibitorExporting ||
                  productExporting ||
                  tmExporting ||
                  oesExporting)
              }
              onClick={() => exportToExcel(activeModalItem, filteredModalData)}
              sx={{ borderRadius: "8px", boxShadow: "none" }}
            >
              Export {currentItem.title} {hasActiveFilter ? `(${filteredModalData.length} Filtered)` : "to Excel"}
            </LoadingButton>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ExportDataPage;
